import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App'

// Suppress harmless Supabase auth network error logs that trigger Readdy error reporter
const _originalConsoleError = console.error;
console.error = function(...args: unknown[]) {
  if (args.length === 1 && args[0] && typeof args[0] === 'object' && Object.keys(args[0]).length === 0) {
    return;
  }
  const msg = String(args[0] ?? '');
  if (msg.includes('fetch failed') || msg.includes('NetworkError') || msg.includes('Failed to fetch') || msg.includes('Network request failed')) {
    return;
  }
  _originalConsoleError.apply(console, args);
};

// ─── Chunk-load error recovery ───────────────────────────────────────────────
const CHUNK_RELOAD_KEY = 'admin_chunk_reload_at';

function isChunkError(msg: string): boolean {
  const m = msg.toLowerCase();
  return (
    m.includes('dynamically imported module') ||
    m.includes('loading chunk') ||
    m.includes('importing a module script failed') ||
    m.includes('error loading')
  );
}

function safeReloadOnChunkError() {
  try {
    const last = localStorage.getItem(CHUNK_RELOAD_KEY);
    if (last && Date.now() - parseInt(last, 10) < 15_000) return;
    localStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch { /* ignore */ }

  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    const ch = new MessageChannel();
    ch.port1.onmessage = () => window.location.reload();
    navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_ALL_CACHES' }, [ch.port2]);
    setTimeout(() => window.location.reload(), 1200);
  } else {
    window.location.reload();
  }
}

window.addEventListener('unhandledrejection', (event) => {
  const msg = event.reason?.message || String(event.reason || '');
  if (isChunkError(msg)) {
    event.preventDefault();
    safeReloadOnChunkError();
  }
});
// ─────────────────────────────────────────────────────────────────────────────

// Service Worker DISABLED.
// A stale service worker (from the previous Readdy host) was intercepting
// navigations and serving cached content, which made links/buttons require
// multiple clicks. We now unregister any existing service worker and clear all
// caches on load, and never register a new one — so every page, element and
// button responds to a single click.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations()
    .then((regs) => regs.forEach((r) => r.unregister()))
    .catch(() => { /* ignore */ });
}
if (typeof caches !== 'undefined' && caches.keys) {
  caches.keys()
    .then((keys) => keys.forEach((k) => caches.delete(k)))
    .catch(() => { /* ignore */ });
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)